import { useEffect, useRef } from "react";
import styled from "styled-components";

import RenderVideo from "../videos/render.mp4";

/* =========================
   CONTAINER PRINCIPAL
========================= */

const MainContainer = styled.div`
  width: 100%;
  box-sizing: border-box;

  padding: 0 80px;

  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 0 32px;
  }

  @media (max-width: 480px) {
    padding: 0 16px;
  }
`;

/* =========================
   SECTION
========================= */

const Section = styled.section`
  width: 100%;
  box-sizing: border-box;

  padding: 48px 24px;
  margin-top: 40px;

  overflow: visible;

  @media (max-width: 768px) {
    padding: 40px 0;
    margin-top: 24px;
  }

  @media (max-width: 480px) {
    padding: 32px 0;
    margin-top: 16px;
  }
`;

/* =========================
   TÍTULO
========================= */

const Title = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;

  margin: 0 0 24px;

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 20px;
  }
`;

/* =========================
   CONTEÚDO
========================= */

const Container = styled.div`
  display: flex;
  align-items: center;

  gap: 48px;

  width: 100%;
  min-width: 0;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;

    gap: 32px;
  }
`;

/* =========================
   TEXTO
========================= */

const Text = styled.div`
  flex: 1;
  min-width: 0;

  color: #374151;

  font-size: 1rem;
  line-height: 1.6;

  position: relative;
  z-index: 2;

  @media (max-width: 767px) {
    width: 100%;
    flex: none;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.65;
  }
`;

/* =========================
   WRAPPER
========================= */

const VideoWrapper = styled.div`
  flex: 1;

  position: relative;

  width: 100%;
  min-width: 0;

  height: 420px;

  overflow: visible;

  @media (max-width: 767px) {
    flex: none;

    height: auto;
    min-height: 260px;

    overflow: hidden;
  }

  @media (max-width: 480px) {
    min-height: 220px;
  }
`;

/* =========================
   VÍDEO ESCONDIDO
========================= */

const HiddenVideo = styled.video`
  position: absolute;

  width: 1px;
  height: 1px;

  opacity: 0;

  pointer-events: none;

  left: -9999px;
  top: -9999px;
`;

/* =========================
   CANVAS VISÍVEL
========================= */

const Canvas = styled.canvas`
  position: absolute;

  left: 50%;
  top: 50%;

  width: ${({ $width }) => $width || "100%"};
  max-width: none;

  height: auto;

  transform:
    translate(-50%, -50%)
    translate(
      ${({ $x }) => $x || "0px"},
      ${({ $y }) => $y || "0px"}
    )
    scale(${({ $scale }) => $scale || 1});

  transform-origin: center;

  display: block;

  pointer-events: none;

  /* fade nas quatro bordas */

  -webkit-mask-image:
    linear-gradient(
      to right,
      transparent 0%,
      black 15%,
      black 85%,
      transparent 100%
    ),
    linear-gradient(
      to bottom,
      transparent 0%,
      black 15%,
      black 85%,
      transparent 100%
    );

  -webkit-mask-composite: source-in;

  mask-image:
    linear-gradient(
      to right,
      transparent 0%,
      black 15%,
      black 85%,
      transparent 100%
    ),
    linear-gradient(
      to bottom,
      transparent 0%,
      black 15%,
      black 85%,
      transparent 100%
    );

  mask-composite: intersect;

  @media (max-width: 767px) {
    position: relative;

    left: auto;
    top: auto;

    width: ${({ $mobileWidth }) => $mobileWidth || "100%"};
    max-width: 100%;

    transform: ${({ $mobileScale }) =>
      `scale(${$mobileScale || 1})`};

    margin: 0 auto;
  }
`;

/* =========================
   COMPONENTE
========================= */

export default function About() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const context = canvas.getContext("2d");

    if (!context) return;

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.playbackRate = 0.7;

    const resizeCanvas = () => {
      if (!video.videoWidth || !video.videoHeight) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    };

    const renderFrame = () => {
      if (
        video.readyState >= 2 &&
        video.videoWidth > 0 &&
        video.videoHeight > 0
      ) {
        if (
          canvas.width !== video.videoWidth ||
          canvas.height !== video.videoHeight
        ) {
          resizeCanvas();
        }

        context.clearRect(
          0,
          0,
          canvas.width,
          canvas.height
        );

        context.drawImage(
          video,
          0,
          0,
          canvas.width,
          canvas.height
        );
      }

      animationRef.current = requestAnimationFrame(renderFrame);
    };

    const startVideo = async () => {
      try {
        await video.play();

        renderFrame();
      } catch (error) {
        console.log("Autoplay bloqueado:", error);
      }
    };

    const handleLoadedMetadata = () => {
      resizeCanvas();
      startVideo();
    };

    video.addEventListener(
      "loadedmetadata",
      handleLoadedMetadata
    );

    if (video.readyState >= 1) {
      handleLoadedMetadata();
    }

    return () => {
      video.removeEventListener(
        "loadedmetadata",
        handleLoadedMetadata
      );

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      video.pause();
    };
  }, []);

  return (
    <MainContainer>
      <Section id="sobre">
        <Title>Sobre a Pólvora</Title>

        <Container>
          <Text>
            Na Pólvora Consultoria, desenvolvemos soluções completas e
            estratégicas para engenharia civil, com foco no gerenciamento de
            obras e gestão integrada de empreendimentos. Nosso compromisso é
            transformar desafios em resultados, unindo planejamento
            inteligente, tecnologia e experiência de mercado.

            <br />
            <br />

            Do planejamento à execução, oferecemos uma gestão eficiente que
            garante qualidade, prazos cumpridos e otimização de recursos.

            <br />
            <br />

            Mais do que entregar projetos, buscamos construir confiança. Cada
            cliente recebe atenção personalizada, com soluções adaptadas às
            suas necessidades e um acompanhamento transparente do início ao
            fim. Para nós, resultado não é apenas um objetivo — é um
            compromisso.
          </Text>

          <VideoWrapper>
            <HiddenVideo
              ref={videoRef}
              src={RenderVideo}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              controls={false}
              disablePictureInPicture
            />

            <Canvas
              ref={canvasRef}

              /* DESKTOP */
              $x="30px"
              $y="0px"
              $scale={1.15}
              $width="105%"

              /* MOBILE */
              $mobileWidth="100%"
              $mobileScale={1}
            />
          </VideoWrapper>
        </Container>
      </Section>
    </MainContainer>
  );
}